import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Sparkles, Check, Wand2 } from "lucide-react";
import { toast } from "sonner";
import type { ManufacturerPreset, DigitalProposalTier, TierKey } from "@/types/digitalProposal";

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  jobId: string;
  jobData: any;
}

const DEFAULT_TIER_NAMES: Record<TierKey, string> = {
  good: "Essential",
  better: "Recommended",
  best: "Premium",
};

const DEFAULT_TERMS =
  "This proposal is valid for 30 days. Final pricing may adjust for unforeseen conditions. Workmanship warranty per manufacturer specifications. Payment due upon completion unless otherwise agreed.";

export function CreateDigitalProposalDialog({ open, onOpenChange, jobId, jobData }: Props) {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [presets, setPresets] = useState<ManufacturerPreset[]>([]);
  const [catalog, setCatalog] = useState<any[]>([]);
  const [squares, setSquares] = useState<number>(0);
  const [estimatePrice, setEstimatePrice] = useState<number>(0);
  const [coverLetter, setCoverLetter] = useState<string>("");
  const [generatingAI, setGeneratingAI] = useState(false);

  // Form state
  const [title, setTitle] = useState("Roof Replacement Proposal");
  const [coverPhotoUrl, setCoverPhotoUrl] = useState("");
  const [scopeSummary, setScopeSummary] = useState(
    "We are pleased to present three options for your roof replacement. Each tier delivers craftsmanship and materials backed by manufacturer warranty."
  );
  const [terms, setTerms] = useState(DEFAULT_TERMS);
  const [financingEnabled, setFinancingEnabled] = useState(true);

  const [tiers, setTiers] = useState<DigitalProposalTier[]>([
    { key: "good", name: "Essential", source: "preset", price: 0, included: [], colors: [] },
    { key: "better", name: "Recommended", source: "preset", price: 0, included: [], colors: [], highlight: true },
    { key: "best", name: "Premium", source: "preset", price: 0, included: [], colors: [] },
  ]);

  const [templateId, setTemplateId] = useState<string | null>(null);
  const [selectedBrochureIds, setSelectedBrochureIds] = useState<string[]>([]);
  const [allBrochures, setAllBrochures] = useState<any[]>([]);

  useEffect(() => {
    if (!open) return;

    (async () => {
      // Load org's digital proposal template (drives branding, brochures, defaults)
      let tpl: any = null;
      if (jobData?.org_id) {
        const { data: t } = await supabase
          .from("digital_proposal_templates")
          .select("*")
          .eq("org_id", jobData.org_id)
          .maybeSingle();
        tpl = t;
        if (tpl) {
          setTemplateId(tpl.id);
          setAllBrochures(tpl.brochures || []);
          if (tpl.default_terms) setTerms(tpl.default_terms);
          if (tpl.financing_enabled !== undefined) setFinancingEnabled(tpl.financing_enabled);
          if (tpl.hero_photo_url) setCoverPhotoUrl(tpl.hero_photo_url);
          if (Array.isArray(tpl.default_tiers) && tpl.default_tiers.length === 3) {
            setTiers(tpl.default_tiers);
          }
        }
      }

      // Load presets (used to enrich tiers when squares known)
      const { data: ps } = await supabase
        .from("manufacturer_presets")
        .select("*")
        .eq("is_active", true)
        .order("manufacturer");
      setPresets((ps as any) || []);

      // Load catalog (org)
      if (jobData?.org_id) {
        const { data: cat } = await supabase
          .from("catalog_items")
          .select("*")
          .eq("org_id", jobData.org_id)
          .eq("is_active", true)
          .order("name");
        setCatalog(cat || []);
      }

      // Load measurements -> squares
      const { data: m } = await supabase
        .from("measurements_roof")
        .select("total_squares")
        .eq("job_id", jobId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      const sq = Number(m?.total_squares) || 0;
      setSquares(sq);

      // Auto-select cover photo from job if available (overrides template hero)
      const { data: photos } = await supabase
        .from("photos")
        .select("url")
        .eq("job_id", jobId)
        .order("created_at", { ascending: false })
        .limit(1);
      if (photos?.[0]?.url) setCoverPhotoUrl(photos[0].url);

      // Pull latest job estimate to seed Better tier price
      const { data: est } = await supabase
        .from("job_estimates")
        .select("customer_price")
        .eq("job_id", jobId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      const price = Number(est?.customer_price) || 0;
      setEstimatePrice(price);

      // Pre-fill title
      if (jobData?.owner_name) {
        setTitle(`Roof Proposal — ${jobData.owner_name}`);
      }
    })();
  }, [open, jobId, jobData]);

  // Seed Good/Better/Best pricing from latest estimate when presets haven't filled them
  useEffect(() => {
    if (estimatePrice <= 0) return;
    setTiers((prev) =>
      prev.map((t) => {
        if (t.price > 0) return t;
        const mult = t.key === "good" ? 0.85 : t.key === "best" ? 1.18 : 1;
        return { ...t, price: Math.round(estimatePrice * mult) };
      })
    );
  }, [estimatePrice]);

  // Auto-fill tiers from preset library when squares are known
  useEffect(() => {
    if (presets.length === 0 || squares <= 0) return;
    setTiers((prev) =>
      prev.map((t) => {
        if (t.source !== "preset" || t.preset_id) return t;
        const preset = presets.find((p) => p.tier === t.key);
        if (!preset) return t;
        return {
          ...t,
          preset_id: preset.id,
          manufacturer: preset.manufacturer,
          product_line: preset.product_line,
          warranty_years: preset.warranty_years,
          price: Math.round((preset.price_per_square || 0) * squares),
          included: preset.features || [],
          colors: preset.colors || [],
        };
      })
    );
  }, [presets, squares]);

  const updateTier = (key: TierKey, patch: Partial<DigitalProposalTier>) => {
    setTiers((prev) => prev.map((t) => (t.key === key ? { ...t, ...patch } : t)));
  };

  const applyPreset = (key: TierKey, presetId: string) => {
    const p = presets.find((x) => x.id === presetId);
    if (!p) return;
    updateTier(key, {
      source: "preset",
      preset_id: presetId,
      manufacturer: p.manufacturer,
      product_line: p.product_line,
      warranty_years: p.warranty_years,
      price: Math.round((p.price_per_square || 0) * (squares || 1)),
      included: p.features || [],
      colors: p.colors || [],
    });
  };

  const handleGenerateAI = async () => {
    setGeneratingAI(true);
    try {
      const better = tiers.find((t) => t.key === "better");
      const { data, error } = await supabase.functions.invoke("generate-proposal-content", {
        body: {
          jobId,
          customerName: jobData?.owner_name || "",
          propertyAddress: [jobData?.property_address, jobData?.property_city, jobData?.property_state]
            .filter(Boolean)
            .join(", "),
          squares,
          scopeSummary,
          selectedTierName: better?.name,
          manufacturer: better?.manufacturer,
          productLine: better?.product_line,
          warrantyYears: better?.warranty_years,
          totalPrice: better?.price,
          companyName: jobData?.org_name || "",
        },
      });
      if (error) throw error;
      if (data?.cover_letter) setCoverLetter(data.cover_letter);
      if (data?.scope_narrative) setScopeSummary(data.scope_narrative);
      toast.success("AI content generated");
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || "Failed to generate content");
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleCreate = async () => {
    setSaving(true);
    try {
      const { data: userRes } = await supabase.auth.getUser();
      const insert = {
        org_id: jobData.org_id,
        job_id: jobId,
        created_by: userRes?.user?.id,
        template_id: templateId,
        selected_brochure_ids: selectedBrochureIds as any,
        title,
        customer_name: jobData?.owner_name || null,
        customer_email: jobData?.owner_email || null,
        customer_phone: jobData?.owner_phone || null,
        property_address: [jobData?.property_address, jobData?.property_city, jobData?.property_state, jobData?.property_zip]
          .filter(Boolean)
          .join(", "),
        cover_photo_url: coverPhotoUrl || null,
        scope_summary: scopeSummary,
        cover_letter_text: coverLetter || null,
        tiers: tiers as any,
        financing_enabled: financingEnabled,
        deposit_enabled: false,
        terms,
        status: "draft",
      };

      const { data, error } = await supabase
        .from("digital_proposals")
        .insert(insert as any)
        .select("id, share_id")
        .single();

      if (error) throw error;
      toast.success("Digital proposal created!");
      onOpenChange(false);
      navigate(`/digital-proposals/review/${data.id}`);
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || "Failed to create proposal");
    } finally {
      setSaving(false);
    }
  };

  const tierByKey = (k: TierKey) => tiers.find((t) => t.key === k)!;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            New Digital Proposal
          </DialogTitle>
          <DialogDescription>
            Build a Good / Better / Best interactive proposal your customer can review, color-pick, sign, and finance online.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {/* Project basics */}
          <Card className="p-4 space-y-3">
            <h3 className="font-semibold text-sm">Project Details</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Title</Label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} />
              </div>
              <div>
                <Label>Cover photo URL</Label>
                <Input
                  value={coverPhotoUrl}
                  onChange={(e) => setCoverPhotoUrl(e.target.value)}
                  placeholder="Auto-filled from job photos"
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label>Personalized cover letter & scope</Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleGenerateAI}
                disabled={generatingAI}
                className="gap-2"
              >
                {generatingAI ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                Generate with AI
              </Button>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Cover letter</Label>
              <Textarea
                rows={4}
                value={coverLetter}
                onChange={(e) => setCoverLetter(e.target.value)}
                placeholder="Click 'Generate with AI' to write a warm, personalized intro for the homeowner."
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Scope summary (shown to customer)</Label>
              <Textarea rows={4} value={scopeSummary} onChange={(e) => setScopeSummary(e.target.value)} />
            </div>
            <div className="text-xs text-muted-foreground flex items-center gap-3">
              <span>Roof area: <span className="font-semibold">{squares > 0 ? `${squares.toFixed(1)} sq` : "not measured"}</span></span>
              {estimatePrice > 0 && (
                <span>· Estimate: <span className="font-semibold">${estimatePrice.toLocaleString()}</span> (seeded into Better tier)</span>
              )}
            </div>
          </Card>

          {/* Tiers */}
          <div>
            <h3 className="font-semibold text-sm mb-3">Good · Better · Best Tiers</h3>
            <Tabs defaultValue="better">
              <TabsList className="grid grid-cols-3 w-full">
                <TabsTrigger value="good">Good · Essential</TabsTrigger>
                <TabsTrigger value="better">Better · Recommended</TabsTrigger>
                <TabsTrigger value="best">Best · Premium</TabsTrigger>
              </TabsList>
              {(["good", "better", "best"] as TierKey[]).map((k) => {
                const t = tierByKey(k);
                const tierPresets = presets.filter((p) => p.tier === k);
                return (
                  <TabsContent key={k} value={k} className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Display name</Label>
                        <Input
                          value={t.name}
                          onChange={(e) => updateTier(k, { name: e.target.value })}
                          placeholder={DEFAULT_TIER_NAMES[k]}
                        />
                      </div>
                      <div>
                        <Label>Total price ($)</Label>
                        <Input
                          type="number"
                          value={t.price}
                          onChange={(e) => updateTier(k, { price: Number(e.target.value) })}
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="mb-2 block">Source</Label>
                      <Tabs
                        value={t.source}
                        onValueChange={(v) => updateTier(k, { source: v as any })}
                      >
                        <TabsList>
                          <TabsTrigger value="preset">Manufacturer preset</TabsTrigger>
                          <TabsTrigger value="catalog">From my catalog</TabsTrigger>
                          <TabsTrigger value="custom">Custom</TabsTrigger>
                        </TabsList>

                        <TabsContent value="preset" className="mt-3">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                            {tierPresets.map((p) => (
                              <Card
                                key={p.id}
                                className={`p-3 cursor-pointer transition-all ${
                                  t.preset_id === p.id ? "border-primary ring-2 ring-primary/30" : "hover:border-primary/50"
                                }`}
                                onClick={() => applyPreset(k, p.id)}
                              >
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-xs font-bold text-primary">{p.manufacturer}</span>
                                  {t.preset_id === p.id && <Check className="w-4 h-4 text-primary" />}
                                </div>
                                <div className="font-semibold text-sm">{p.product_line}</div>
                                <div className="text-xs text-muted-foreground mt-1">
                                  {p.warranty_years}-yr • ${p.price_per_square}/sq
                                </div>
                                <div className="flex gap-1 mt-2">
                                  {(p.colors || []).slice(0, 5).map((c) => (
                                    <div
                                      key={c.hex}
                                      className="w-4 h-4 rounded-full border"
                                      style={{ backgroundColor: c.hex }}
                                      title={c.name}
                                    />
                                  ))}
                                </div>
                              </Card>
                            ))}
                          </div>
                        </TabsContent>

                        <TabsContent value="catalog" className="mt-3">
                          <div className="text-xs text-muted-foreground mb-2">
                            Pick catalog items to bundle into this tier (price autosums).
                          </div>
                          <div className="max-h-48 overflow-y-auto border rounded-md divide-y">
                            {catalog.length === 0 ? (
                              <div className="p-3 text-sm text-muted-foreground">No catalog items yet.</div>
                            ) : (
                              catalog.map((item) => {
                                const checked = t.catalog_item_ids?.includes(item.id);
                                return (
                                  <label key={item.id} className="flex items-center gap-2 p-2 cursor-pointer hover:bg-muted/40">
                                    <input
                                      type="checkbox"
                                      checked={!!checked}
                                      onChange={(e) => {
                                        const set = new Set(t.catalog_item_ids || []);
                                        if (e.target.checked) set.add(item.id);
                                        else set.delete(item.id);
                                        const ids = Array.from(set);
                                        const sumPrice = ids.reduce((s, id) => {
                                          const it = catalog.find((c) => c.id === id);
                                          return s + (Number(it?.unit_price) || 0);
                                        }, 0);
                                        updateTier(k, { catalog_item_ids: ids, price: sumPrice || t.price });
                                      }}
                                    />
                                    <span className="text-sm flex-1">{item.name}</span>
                                    <span className="text-xs text-muted-foreground">
                                      ${Number(item.unit_price || 0).toFixed(2)}
                                    </span>
                                  </label>
                                );
                              })
                            )}
                          </div>
                        </TabsContent>

                        <TabsContent value="custom" className="mt-3">
                          <Label>What's included (one per line)</Label>
                          <Textarea
                            rows={5}
                            value={(t.included || []).join("\n")}
                            onChange={(e) =>
                              updateTier(k, { included: e.target.value.split("\n").filter(Boolean) })
                            }
                          />
                        </TabsContent>
                      </Tabs>
                    </div>

                    {t.included.length > 0 && (
                      <div className="text-xs">
                        <span className="font-semibold">Included:</span>{" "}
                        {t.included.map((f, i) => (
                          <Badge key={i} variant="secondary" className="ml-1 my-0.5">
                            {f}
                          </Badge>
                        ))}
                      </div>
                    )}

                    {t.colors.length > 0 && (
                      <div>
                        <div className="text-xs font-semibold mb-1">Color options offered to customer:</div>
                        <div className="flex flex-wrap gap-2">
                          {t.colors.map((c) => (
                            <div key={c.hex} className="flex items-center gap-1 px-2 py-1 border rounded-full text-xs">
                              <div className="w-3 h-3 rounded-full border" style={{ backgroundColor: c.hex }} />
                              {c.name}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </TabsContent>
                );
              })}
            </Tabs>
          </div>

          {/* Settings */}
          <Card className="p-4 space-y-3">
            <h3 className="font-semibold text-sm">Customer Actions</h3>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm">Enable financing (Hearth)</Label>
                <p className="text-xs text-muted-foreground">Customer can apply for financing in one click.</p>
              </div>
              <Switch checked={financingEnabled} onCheckedChange={setFinancingEnabled} />
            </div>
            <div>
              <Label>Terms &amp; conditions</Label>
              <Textarea rows={3} value={terms} onChange={(e) => setTerms(e.target.value)} />
            </div>
          </Card>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Create &amp; Preview
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
